import { featureFlags } from '../config/brand';
import { DEFAULT_DESIGN_OPACITY } from '../../shared/catalog';
import { catalogAssetCacheKey, subscribeCatalogAssetInvalidation } from '../api/assets';
import { applyPixelAdjustments, hasColorAdjustments, supportsCanvasFilters, type PixelAdjustments } from './canvasAdjustments';
import { analyzeArtworkBackground, applyArtworkBackgroundAnalysis, type ArtworkBackgroundAnalysis } from './backgroundRemoval';
import { EditorHistory } from './history';
import { consumeDecodedImage, decodeImageFile, isCoarsePointer, loadNormalizedArtwork, releaseDecodedImage, releaseNormalizedArtwork, sourceSize } from './image';
import { EditorInteractionMode } from './interactionMode';
import { frontToBack, reorderLayers, type LayerMove } from './layerOrder';
import type { AddLayerInput, EditorAdjustmentDiagnostic, EditorSnapshot, EditorState, EngineCallbacks, MaskMode, Point, TattooLayer } from './types';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const clone = <T,>(value: T): T => structuredClone(value);
const normalizeAngle = (value: number) => ((value + 180) % 360 + 360) % 360 - 180;
const distance = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);
const angle = (a: Point, b: Point) => Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
const radians = (degrees: number) => degrees * Math.PI / 180;
const releaseCanvas = (canvas: HTMLCanvasElement): void => { canvas.width = 0; canvas.height = 0; };
const destroyedError = (): Error => { const error = new Error('The editor is no longer active.'); error.name = 'AbortError'; return error; };

export const normalizedToCanvasPoint = (point: Point, width: number, height: number): Point => ({ x: point.x * width, y: point.y * height });
export const rotateNormalizedPoint90 = (point: Point): Point => ({ x: 1 - point.y, y: point.x });
export const translateNormalizedPoint = (point: Point, delta: Point, width: number, height: number): Point => ({
  x: clamp(point.x + delta.x / width, -1, 2),
  y: clamp(point.y + delta.y / height, -1, 2),
});

const defaultPhoto = (): EditorState['photo'] => ({
  brightness: 100,
  contrast: 100,
  saturation: 100,
  warmth: 0,
  rotation: 0,
  flipX: 1,
  zoom: 100,
  offsetX: 0,
  offsetY: 0,
});

const defaultState = (): EditorState => ({ photo: defaultPhoto(), layers: [], selectedLayerId: null });

interface LayerGeometry {
  width: number;
  height: number;
  matrix: DOMMatrix;
  inverse: DOMMatrix;
  corners: Point[];
  rotationHandle: Point;
}

interface AdjustmentCache {
  key: string;
  canvas: HTMLCanvasElement;
}

type Gesture =
  | { type: 'drag'; origin: Point; layerX: number; layerY: number }
  | { type: 'scale'; originDistance: number; scale: number }
  | { type: 'rotate'; originAngle: number; rotation: number }
  | { type: 'pinch'; distance: number; angle: number; scale: number; rotation: number }
  | { type: 'mask'; strokeIndex: number };

export class CanvasEngine {
  readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly callbacks: EngineCallbacks;
  private state: EditorState = defaultState();
  private photo: HTMLCanvasElement | null = null;
  private sourceWidth = 0;
  private sourceHeight = 0;
  private readonly images = new Map<string, HTMLCanvasElement>();
  private readonly imageLoads = new Map<string, Promise<HTMLCanvasElement>>();
  private readonly backgroundAnalysisCache = new Map<string, ArtworkBackgroundAnalysis>();
  private backgroundAnalysisBuilds = 0;
  private readonly baseCache = new Map<string, HTMLCanvasElement>();
  private readonly layerCache = new Map<string, { key: string; canvas: HTMLCanvasElement }>();
  private readonly layerAdjustmentCache = new Map<string, AdjustmentCache>();
  private photoAdjustmentCache: AdjustmentCache | null = null;
  private readonly nativeCanvasFilters: boolean;
  private readonly pendingAdjustmentDiagnostics = new Map<string, Omit<EditorAdjustmentDiagnostic, 'stage' | 'renderer'>>();
  private readonly history = new EditorHistory(featureFlags.editorHistorySafetyLimit);
  private estimatedHistoryBytes = 0;
  private frame = 0;
  private previewMode = false;
  private pointers = new Map<number, Point>();
  private gesture: Gesture | null = null;
  private readonly interactionMode = new EditorInteractionMode();
  private maskBrushSize = 14;
  private watermark = new Image();
  private resizeObserver?: ResizeObserver;
  private readonly assetRefreshVersions = new Map<string, number>();
  private readonly unsubscribeCatalogAssetInvalidation: () => void;
  private readonly eventListeners: Array<{ type: string; listener: EventListener }> = [];
  private destroyed = false;
  private photoOperationGeneration = 0;
  private designOperationGeneration = 0;

  constructor(canvas: HTMLCanvasElement, callbacks: EngineCallbacks = {}) {
    this.canvas = canvas;
    const context = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!context) throw new Error('Canvas 2D is unavailable.');
    this.context = context;
    this.nativeCanvasFilters = supportsCanvasFilters(context);
    this.callbacks = callbacks;
    this.unsubscribeCatalogAssetInvalidation = subscribeCatalogAssetInvalidation((cacheKey) => {
      const sources = [...this.images.keys()].filter((url) => catalogAssetCacheKey(url) === cacheKey);
      if (this.destroyed || !sources.length) return;
      void Promise.all(sources.map(async (url) => {
        const version = (this.assetRefreshVersions.get(url) || 0) + 1;
        this.assetRefreshVersions.set(url, version);
        const normalized = await loadNormalizedArtwork(url);
        let owned: HTMLCanvasElement;
        try { owned = this.copyArtwork(normalized.canvas); }
        finally { releaseNormalizedArtwork(url, normalized.canvas); }
        if (this.destroyed || this.assetRefreshVersions.get(url) !== version) {
          releaseCanvas(owned);
          return false;
        }
        const previous = this.images.get(url);
        this.releaseArtworkRenderCaches();
        this.images.set(url, owned);
        if (previous) releaseCanvas(previous);
        return true;
      })).then((refreshed) => {
        if (!refreshed.some(Boolean)) return;
        this.render();
      }).catch((error) => this.callbacks.onError?.(error instanceof Error ? error.message : 'The updated design could not be decoded.'));
    });
    this.watermark.onload = () => this.render();
    this.watermark.src = '/branding/walld-mark-dark.svg';
    this.bindGestures();
    this.bindKeyboard();
    if ('ResizeObserver' in window && canvas.parentElement) {
      this.resizeObserver = new ResizeObserver(() => this.fitDisplay());
      this.resizeObserver.observe(canvas.parentElement);
    }
    this.history.reset(this.state);
    this.updateHistoryMetrics();
    this.render();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.photoOperationGeneration += 1;
    this.designOperationGeneration += 1;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.unsubscribeCatalogAssetInvalidation();
    for (const { type, listener } of this.eventListeners) this.canvas.removeEventListener(type, listener);
    this.eventListeners.length = 0;
    this.callbacks.onInteractionActive?.(false);
    this.pointers.clear();
    this.gesture = null;
    this.interactionMode.leaveContext();
    this.watermark.onload = null;
    this.watermark.src = '';
    const canvases = new Set<HTMLCanvasElement>();
    if (this.photo) canvases.add(this.photo);
    for (const canvas of this.baseCache.values()) canvases.add(canvas);
    for (const { canvas } of this.layerCache.values()) canvases.add(canvas);
    for (const { canvas } of this.layerAdjustmentCache.values()) canvases.add(canvas);
    if (this.photoAdjustmentCache) canvases.add(this.photoAdjustmentCache.canvas);
    for (const canvas of this.images.values()) canvases.add(canvas);
    for (const canvas of canvases) releaseCanvas(canvas);
    this.photo = null;
    this.images.clear();
    this.imageLoads.clear();
    this.backgroundAnalysisCache.clear();
    this.baseCache.clear();
    this.layerCache.clear();
    this.layerAdjustmentCache.clear();
    this.photoAdjustmentCache = null;
    this.pendingAdjustmentDiagnostics.clear();
    this.assetRefreshVersions.clear();
    this.state = defaultState();
    this.history.reset(this.state);
    this.estimatedHistoryBytes = 0;
    this.sourceWidth = 0;
    this.sourceHeight = 0;
    this.canvas.width = 0;
    this.canvas.height = 0;
  }

  getState(): EditorState { return clone(this.state); }
  hasPhoto(): boolean { return Boolean(this.photo); }
  hasLayers(): boolean { return this.state.layers.length > 0; }
  getPhotoInfo(): { width: number; height: number } { return { width: this.sourceWidth, height: this.sourceHeight }; }
  getSelectedLayer(): TattooLayer | null {
    return this.state.layers.find((layer) => layer.id === this.state.selectedLayerId) || null;
  }

  private snapshot(): EditorSnapshot {
    return {
      state: this.getState(),
      canUndo: this.history.canUndo,
      canRedo: this.history.canRedo,
      hasPhoto: this.hasPhoto(),
      hasLayers: this.hasLayers(),
      maskMode: this.interactionMode.value,
      maskBrushSize: this.maskBrushSize,
      history: {
        length: this.history.length,
        index: this.history.index,
        safetyLimit: this.history.safetyLimit,
        ...(import.meta.env.DEV ? { estimatedBytes: this.estimatedHistoryBytes } : {}),
      },
    };
  }

  private notify(): void { this.callbacks.onChange?.(this.snapshot()); }
  private baseCacheKey(layer: Pick<TattooLayer, 'sourceUrl' | 'whiteRemoval' | 'whiteThreshold'>): string {
    return `${layer.sourceUrl}|${layer.whiteRemoval}|${layer.whiteThreshold}`;
  }

  private dropLayerCache(id: string): void {
    const cached = this.layerCache.get(id);
    if (!cached) return;
    this.layerCache.delete(id);
    const ownedByBaseCache = [...this.baseCache.values()].some((canvas) => canvas === cached.canvas);
    const sharedByAnotherLayer = [...this.layerCache.values()].some(({ canvas }) => canvas === cached.canvas);
    if (!ownedByBaseCache && !sharedByAnotherLayer) releaseCanvas(cached.canvas);
  }

  private dropAllLayerCaches(): void {
    for (const id of [...this.layerCache.keys()]) this.dropLayerCache(id);
  }

  private pruneUnusedBaseCache(): void {
    const activeKeys = new Set(this.state.layers.map((layer) => this.baseCacheKey(layer)));
    for (const [key, canvas] of this.baseCache) {
      if (activeKeys.has(key)) continue;
      // A no-mask layer cache borrows the processed base canvas. Remove every
      // stale borrower before releasing the variant's backing store.
      for (const [layerId, cached] of this.layerCache) {
        if (cached.canvas === canvas) this.layerCache.delete(layerId);
      }
      this.baseCache.delete(key);
      releaseCanvas(canvas);
    }
  }

  private invalidateLayer(id: string): void {
    this.dropLayerCache(id);
    const adjusted = this.layerAdjustmentCache.get(id);
    if (adjusted) adjusted.key = '';
  }

  private updateHistoryMetrics(): void {
    if (import.meta.env.DEV) this.estimatedHistoryBytes = this.history.estimatedBytes();
  }

  private commit(force = false): void {
    if (this.history.record(this.state, force)) this.updateHistoryMetrics();
    this.notify();
  }

  beginHistoryTransaction(): void { this.history.begin(this.state); }
  commitHistoryTransaction(): void {
    if (this.history.commit(this.state)) this.updateHistoryMetrics();
    this.notify();
  }

  async undo(): Promise<void> { await this.restoreHistory('undo'); }
  async redo(): Promise<void> { await this.restoreHistory('redo'); }

  private async restoreHistory(direction: 'undo' | 'redo'): Promise<void> {
    const snapshot = direction === 'undo' ? this.history.undo() : this.history.redo();
    if (!snapshot) return;
    const generation = ++this.designOperationGeneration;
    for (const layer of snapshot.layers) await this.ensureImage(layer.sourceUrl);
    if (this.destroyed || generation !== this.designOperationGeneration) return;
    this.state = snapshot;
    this.dropAllLayerCaches();
    this.pruneUnusedBaseCache();
    this.render();
    this.notify();
  }

  async loadPhoto(file: File, photoId?: string): Promise<void> {
    await this.prepareAndCommitPhoto(file, false, photoId);
  }

  async replacePhoto(file: File, photoId?: string): Promise<void> {
    await this.prepareAndCommitPhoto(file, true, photoId);
  }

  private async prepareAndCommitPhoto(file: File, preserveComposition: boolean, photoId?: string): Promise<void> {
    const generation = ++this.photoOperationGeneration;
    // Anything started against the previous body photo must not commit later.
    this.designOperationGeneration += 1;
    const lowMemory = Number((navigator as Navigator & { deviceMemory?: number }).deviceMemory || 4) <= 2;
    const workingEdge = lowMemory ? 1500 : isCoarsePointer() ? 1800 : 2400;
    const decoded = await decodeImageFile(file, workingEdge, {
      onDiagnostic: this.callbacks.onPhotoDiagnostic,
      shouldContinue: () => !this.destroyed && generation === this.photoOperationGeneration,
      photoId,
    });
    if (this.destroyed || generation !== this.photoOperationGeneration) {
      releaseDecodedImage(decoded);
      throw destroyedError();
    }
    const { width, height } = sourceSize(decoded);
    const nextPhoto = consumeDecodedImage(decoded, workingEdge);
    if (this.destroyed || generation !== this.photoOperationGeneration) { releaseCanvas(nextPhoto); throw destroyedError(); }

    const previousPhoto = this.photo;
    const nextState = preserveComposition ? clone(this.state) : defaultState();
    nextState.photo = defaultPhoto();
    if (nextState.selectedLayerId && !nextState.layers.some((layer) => layer.id === nextState.selectedLayerId)) {
      nextState.selectedLayerId = nextState.layers.at(-1)?.id || null;
    }

    // Atomic commit: no existing photo, layer, selection, or cache is touched
    // until the replacement has decoded and its working canvas is ready.
    this.photo = nextPhoto;
    if (this.photoAdjustmentCache) releaseCanvas(this.photoAdjustmentCache.canvas);
    this.photoAdjustmentCache = null;
    this.sourceWidth = width;
    this.sourceHeight = height;
    this.canvas.width = this.photo.width;
    this.canvas.height = this.photo.height;
    this.state = nextState;
    this.resetInteractionMode();
    this.history.reset(this.state);
    this.updateHistoryMetrics();
    this.notify();
    this.fitDisplay();
    this.render();
    if (previousPhoto) releaseCanvas(previousPhoto);
  }

  private async ensureImage(url: string): Promise<HTMLCanvasElement> {
    if (this.destroyed) throw destroyedError();
    const cached = this.images.get(url);
    if (cached) return cached;
    const pending = this.imageLoads.get(url);
    if (pending) return pending;
    const created = (async () => {
      const normalized = await loadNormalizedArtwork(url);
      if (this.destroyed) { releaseNormalizedArtwork(url, normalized.canvas); throw destroyedError(); }
      let owned: HTMLCanvasElement;
      try { owned = this.copyArtwork(normalized.canvas); }
      finally { releaseNormalizedArtwork(url, normalized.canvas); }
      if (this.destroyed) { releaseCanvas(owned); throw destroyedError(); }
      const existing = this.images.get(url);
      if (existing) { releaseCanvas(owned); return existing; }
      this.images.set(url, owned);
      return owned;
    })();
    this.imageLoads.set(url, created);
    try { return await created; }
    finally { if (this.imageLoads.get(url) === created) this.imageLoads.delete(url); }
  }

  private copyArtwork(source: HTMLCanvasElement): HTMLCanvasElement {
    if (source.width < 1 || source.height < 1) throw new Error('The selected design is no longer available.');
    const owned = document.createElement('canvas');
    owned.width = source.width;
    owned.height = source.height;
    const context = owned.getContext('2d');
    if (!context) { releaseCanvas(owned); throw new Error('Canvas 2D is unavailable.'); }
    context.drawImage(source, 0, 0);
    return owned;
  }

  private releaseArtworkRenderCaches(): void {
    const canvases = new Set<HTMLCanvasElement>();
    for (const canvas of this.baseCache.values()) canvases.add(canvas);
    for (const { canvas } of this.layerCache.values()) canvases.add(canvas);
    for (const { canvas } of this.layerAdjustmentCache.values()) canvases.add(canvas);
    for (const canvas of canvases) releaseCanvas(canvas);
    this.baseCache.clear();
    this.layerCache.clear();
    this.layerAdjustmentCache.clear();
    this.backgroundAnalysisCache.clear();
  }

  async restoreState(state: EditorState): Promise<void> {
    if (!this.photo || this.destroyed) throw destroyedError();
    const generation = ++this.designOperationGeneration;
    await Promise.all(state.layers.map((layer) => this.ensureImage(layer.sourceUrl)));
    if (this.destroyed || generation !== this.designOperationGeneration) throw destroyedError();
    this.state = clone(state);
    this.resetInteractionMode();
    this.releaseArtworkRenderCaches();
    this.history.reset(this.state);
    this.updateHistoryMetrics();
    this.notify();
    this.fitDisplay();
    this.render();
  }

  async addLayer(input: AddLayerInput): Promise<string> {
    if (!this.photo) throw new Error('Upload a photo first.');
    const generation = ++this.designOperationGeneration;
    await this.ensureImage(input.sourceUrl);
    if (this.destroyed || generation !== this.designOperationGeneration || !this.photo) throw destroyedError();
    this.resetInteractionMode();
    const whiteRemovalDefault = true;
    const number = this.state.layers.length + 1;
    const id = crypto.randomUUID?.() || `layer-${Date.now()}-${number}`;
    const layer: TattooLayer = {
      id,
      designId: input.designId,
      name: input.name || `Tattoo ${number}`,
      sourceUrl: input.sourceUrl,
      isCustom: Boolean(input.isCustom),
      x: .5,
      y: .5,
      scale: 34,
      scaleX: 100,
      scaleY: 100,
      rotation: 0,
      skewX: 0,
      skewY: 0,
      opacity: input.opacity ?? 100,
      brightness: 100,
      contrast: 112,
      saturation: 100,
      hue: 0,
      softness: 0,
      blendMode: input.blendMode ?? 'multiply',
      flipX: 1,
      flipY: 1,
      whiteRemoval: whiteRemovalDefault,
      whiteRemovalDefault,
      whiteThreshold: input.whiteThreshold ?? 232,
      visible: true,
      locked: false,
      maskStrokes: [],
    };
    this.state.layers.push(layer);
    this.state.selectedLayerId = id;
    this.commit();
    this.render();
    return id;
  }

  async replaceSelected(input: AddLayerInput): Promise<void> {
    const layer = this.getSelectedLayer();
    if (!layer) { await this.addLayer(input); return; }
    const targetId = layer.id;
    const generation = ++this.designOperationGeneration;
    await this.ensureImage(input.sourceUrl);
    if (this.destroyed || generation !== this.designOperationGeneration) throw destroyedError();
    const target = this.state.layers.find((item) => item.id === targetId);
    if (!target) throw destroyedError();
    this.resetInteractionMode();
    const whiteRemovalDefault = true;
    Object.assign(target, {
      designId: input.designId,
      name: input.name,
      sourceUrl: input.sourceUrl,
      isCustom: Boolean(input.isCustom),
      opacity: input.opacity ?? target.opacity,
      blendMode: input.blendMode ?? target.blendMode,
      whiteRemoval: whiteRemovalDefault,
      whiteRemovalDefault,
      whiteThreshold: input.whiteThreshold ?? target.whiteThreshold,
      maskStrokes: [],
    });
    this.invalidateLayer(target.id);
    this.pruneUnusedBaseCache();
    this.commit();
    this.render();
  }

  selectLayer(id: string | null): void {
    if (id && !this.state.layers.some((layer) => layer.id === id)) return;
    if (this.interactionMode.selectLayer(this.state.selectedLayerId, id)) {
      this.canvas.style.cursor = '';
      this.hideBrushCursor();
    }
    this.state.selectedLayerId = id;
    this.render();
    this.notify();
  }

  updateLayer(patch: Partial<TattooLayer>, commit = true): void {
    const layer = this.getSelectedLayer();
    if (!layer || layer.locked) return;
    const before = import.meta.env.DEV && this.callbacks.onAdjustmentDiagnostic ? clone(layer) : null;
    if (!commit) this.beginHistoryTransaction();
    Object.assign(layer, patch);
    layer.x = clamp(Number(layer.x), -1, 2);
    layer.y = clamp(Number(layer.y), -1, 2);
    layer.scale = clamp(Number(layer.scale), 3, 250);
    layer.scaleX = clamp(Number(layer.scaleX), 20, 250);
    layer.scaleY = clamp(Number(layer.scaleY), 20, 250);
    layer.rotation = normalizeAngle(Number(layer.rotation));
    layer.skewX = clamp(Number(layer.skewX), -45, 45);
    layer.skewY = clamp(Number(layer.skewY), -45, 45);
    layer.opacity = clamp(Number(layer.opacity), 5, 100);
    layer.brightness = clamp(Number(layer.brightness), 20, 200);
    layer.contrast = clamp(Number(layer.contrast), 20, 220);
    layer.saturation = clamp(Number(layer.saturation), 0, 200);
    layer.hue = clamp(Number(layer.hue), -180, 180);
    layer.softness = clamp(Number(layer.softness), 0, 8);
    layer.whiteThreshold = clamp(Number(layer.whiteThreshold), 180, 255);
    const baseVariantChanged = 'whiteRemoval' in patch || 'whiteThreshold' in patch;
    if (baseVariantChanged || 'maskStrokes' in patch) this.invalidateLayer(layer.id);
    if (baseVariantChanged) this.pruneUnusedBaseCache();
    if (before) this.trackAdjustmentChanges('layer', layer.id, before, layer, Object.keys(patch));
    this.render();
    if (commit) {
      if (this.history.transactionActive) this.commitHistoryTransaction(); else this.commit();
    } else this.notify();
  }

  updatePhoto(patch: Partial<EditorState['photo']>, commit = true): void {
    const before = import.meta.env.DEV && this.callbacks.onAdjustmentDiagnostic ? clone(this.state.photo) : null;
    if (!commit) this.beginHistoryTransaction();
    Object.assign(this.state.photo, patch);
    this.state.photo.brightness = clamp(Number(this.state.photo.brightness), 50, 150);
    this.state.photo.contrast = clamp(Number(this.state.photo.contrast), 50, 150);
    this.state.photo.saturation = clamp(Number(this.state.photo.saturation), 0, 180);
    this.state.photo.warmth = clamp(Number(this.state.photo.warmth), -50, 50);
    this.state.photo.zoom = clamp(Number(this.state.photo.zoom), 80, 180);
    if (before) this.trackAdjustmentChanges('photo', 'photo', before, this.state.photo, Object.keys(patch));
    this.render();
    if (commit) {
      if (this.history.transactionActive) this.commitHistoryTransaction(); else this.commit();
    } else this.notify();
  }

  rotatePhoto90(): void {
    if (!this.photo) return;
    const oldWidth = this.canvas.width;
    const oldHeight = this.canvas.height;
    this.state.photo.rotation = ((this.state.photo.rotation + 90) % 360) as EditorState['photo']['rotation'];
    this.canvas.width = oldHeight;
    this.canvas.height = oldWidth;
    for (const layer of this.state.layers) {
      const rotated = rotateNormalizedPoint90(layer);
      layer.x = rotated.x;
      layer.y = rotated.y;
      layer.rotation = normalizeAngle(layer.rotation + 90);
    }
    this.state.photo.offsetX = 0;
    this.state.photo.offsetY = 0;
    this.commit();
    this.fitDisplay();
    this.render();
  }

  flipPhoto(): void { this.state.photo.flipX *= -1; this.commit(); this.render(); }
  resetPhoto(): void {
    const rotation = this.state.photo.rotation;
    if ((rotation === 90 || rotation === 270) && this.photo) {
      this.canvas.width = this.photo.width;
      this.canvas.height = this.photo.height;
    }
    this.state.photo = defaultPhoto();
    this.commit();
    this.fitDisplay();
    this.render();
  }

  rotateSelected90(): void {
    const layer = this.getSelectedLayer();
    if (layer && !layer.locked) this.updateLayer({ rotation: layer.rotation + 90 }, true);
  }

  centerSelected(): void {
    const layer = this.getSelectedLayer();
    if (!layer || layer.locked) return;
    layer.x = .5;
    layer.y = .5;
    this.commit();
    this.render();
  }

  resetSelected(): void {
    const layer = this.getSelectedLayer();
    if (!layer || layer.locked) return;
    Object.assign(layer, {
      x: .5, y: .5, scale: 34, scaleX: 100, scaleY: 100,
      rotation: 0, skewX: 0, skewY: 0, opacity: DEFAULT_DESIGN_OPACITY, brightness: 100, contrast: 112,
      saturation: 100, hue: 0, softness: 0, blendMode: 'multiply', flipX: 1, flipY: 1,
      whiteRemoval: layer.whiteRemovalDefault, whiteThreshold: 232, maskStrokes: [],
    });
    this.invalidateLayer(layer.id);
    this.pruneUnusedBaseCache();
    this.commit();
    this.render();
  }

  duplicateSelected(): void {
    const layer = this.getSelectedLayer();
    if (!layer) return;
    const duplicate = clone(layer);
    duplicate.id = crypto.randomUUID?.() || `layer-${Date.now()}`;
    duplicate.name = `${layer.name} copy`;
    duplicate.x = clamp(duplicate.x + .04, -1, 2);
    duplicate.y = clamp(duplicate.y + .04, -1, 2);
    duplicate.locked = false;
    this.state.layers.push(duplicate);
    this.state.selectedLayerId = duplicate.id;
    this.commit();
    this.render();
  }

  deleteSelected(): void {
    if (this.state.selectedLayerId) this.deleteLayer(this.state.selectedLayerId);
  }

  deleteLayer(id: string): void {
    const index = this.state.layers.findIndex((layer) => layer.id === id);
    if (index < 0) return;
    const [removed] = this.state.layers.splice(index, 1);
    this.resetInteractionMode();
    this.dropLayerCache(removed.id);
    const adjustment = this.layerAdjustmentCache.get(removed.id);
    if (adjustment) releaseCanvas(adjustment.canvas);
    this.layerAdjustmentCache.delete(removed.id);
    this.pruneUnusedBaseCache();
    if (this.state.selectedLayerId === id) this.state.selectedLayerId = this.state.layers[Math.min(index, this.state.layers.length - 1)]?.id || null;
    this.commit();
    this.render();
  }

  toggleLayerVisibility(id: string): void {
    const layer = this.state.layers.find((item) => item.id === id);
    if (!layer) return;
    layer.visible = !layer.visible;
    this.commit();
    this.render();
  }

  toggleLayerLock(id: string): void {
    const layer = this.state.layers.find((item) => item.id === id);
    if (!layer) return;
    layer.locked = !layer.locked;
    this.commit();
    this.render();
  }

  moveLayer(id: string, action: LayerMove): void {
    if (!this.state.layers.some((layer) => layer.id === id)) return;
    this.state.layers = reorderLayers(this.state.layers, id, action);
    this.commit();
    this.render();
  }

  setMaskMode(mode: MaskMode): void {
    if (!this.interactionMode.set(mode)) return;
    this.canvas.style.cursor = mode === 'off' ? '' : 'none';
    if (mode === 'off') this.hideBrushCursor();
    this.render();
    this.notify();
  }
  getMaskMode(): MaskMode { return this.interactionMode.value; }
  setMaskBrushSize(size: number): void { this.maskBrushSize = clamp(size, 4, 40); this.notify(); }
  resetInteractionMode(): void { this.setMaskMode('off'); }
  clearMask(): void {
    const layer = this.getSelectedLayer();
    if (!layer || layer.locked || !layer.maskStrokes.length) return;
    layer.maskStrokes = [];
    this.invalidateLayer(layer.id);
    this.commit();
    this.render();
  }

  resetComposition(): void {
    if (!this.photo) return;
    this.state = defaultState();
    this.resetInteractionMode();
    this.releaseArtworkRenderCaches();
    this.history.reset(this.state);
    this.updateHistoryMetrics();
    this.notify();
    this.render();
  }

  setPreviewMode(active: boolean): void { if (active) this.resetInteractionMode(); this.previewMode = active; this.render(); }

  private trackAdjustmentChanges(
    target: 'photo' | 'layer',
    targetId: string,
    before: object,
    after: object,
    parameters: string[],
  ): void {
    if (!import.meta.env.DEV || !this.callbacks.onAdjustmentDiagnostic) return;
    const previous = before as Record<string, unknown>;
    const current = after as Record<string, unknown>;
    for (const parameter of parameters) {
      if (Object.is(previous[parameter], current[parameter])) continue;
      const diagnostic = { target, targetId, parameter, before: previous[parameter], after: current[parameter] };
      this.pendingAdjustmentDiagnostics.set(`${target}:${targetId}:${parameter}`, diagnostic);
      this.callbacks.onAdjustmentDiagnostic({ ...diagnostic, stage: 'state_changed' });
    }
  }

  private flushAdjustmentDiagnostics(target: 'photo' | 'layer', targetId: string): void {
    if (!import.meta.env.DEV || !this.callbacks.onAdjustmentDiagnostic) return;
    const filterParameters = new Set(['brightness', 'contrast', 'saturation', 'hue', 'softness']);
    const compositeParameters = new Set(['opacity', 'blendMode', 'warmth']);
    for (const [key, diagnostic] of this.pendingAdjustmentDiagnostics) {
      if (diagnostic.target !== target || diagnostic.targetId !== targetId) continue;
      const renderer = filterParameters.has(diagnostic.parameter)
        ? this.nativeCanvasFilters ? 'native_filter' : 'pixel_fallback'
        : diagnostic.parameter === 'whiteThreshold' || diagnostic.parameter === 'whiteRemoval'
          ? 'pixel_threshold'
          : compositeParameters.has(diagnostic.parameter) ? 'composite' : 'transform';
      this.callbacks.onAdjustmentDiagnostic({ ...diagnostic, stage: 'render_applied', renderer });
      this.pendingAdjustmentDiagnostics.delete(key);
    }
  }

  private fallbackAdjustedCanvas(
    source: HTMLCanvasElement,
    adjustments: PixelAdjustments,
    key: string,
    current: AdjustmentCache | null,
  ): { source: HTMLCanvasElement; cache: AdjustmentCache | null } {
    if (!hasColorAdjustments(adjustments)) {
      if (current) releaseCanvas(current.canvas);
      return { source, cache: null };
    }
    if (current?.key === key) return { source: current.canvas, cache: current };
    const canvas = current?.canvas || document.createElement('canvas');
    if (canvas.width !== source.width || canvas.height !== source.height) {
      canvas.width = source.width;
      canvas.height = source.height;
    }
    const context = canvas.getContext('2d', { willReadFrequently: true });
    // Incomplete test doubles can omit pixel APIs; every real 2D context has both.
    if (!context || typeof context.getImageData !== 'function' || typeof context.putImageData !== 'function') {
      if (!current) releaseCanvas(canvas);
      return { source, cache: current };
    }
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.globalAlpha = 1;
    context.globalCompositeOperation = 'source-over';
    context.drawImage(source, 0, 0);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    applyPixelAdjustments(pixels.data, canvas.width, canvas.height, adjustments);
    context.putImageData(pixels, 0, 0);
    const cache = { key, canvas };
    return { source: canvas, cache };
  }

  private adjustedPhotoSource(photo: EditorState['photo']): HTMLCanvasElement {
    if (!this.photo || this.nativeCanvasFilters) return this.photo!;
    const adjustments = { brightness: photo.brightness, contrast: photo.contrast, saturation: photo.saturation };
    const key = `${this.photo.width}x${this.photo.height}|${photo.brightness}|${photo.contrast}|${photo.saturation}`;
    const adjusted = this.fallbackAdjustedCanvas(this.photo, adjustments, key, this.photoAdjustmentCache);
    this.photoAdjustmentCache = adjusted.cache;
    return adjusted.source;
  }

  private adjustedLayerSource(layer: TattooLayer, source: HTMLCanvasElement): HTMLCanvasElement {
    if (this.nativeCanvasFilters) return source;
    const geometry = this.layerGeometry(layer);
    const blurScaleX = source.width / Math.max(1, geometry.width);
    const blurScaleY = source.height / Math.max(1, geometry.height);
    const blurRadius = layer.softness * (blurScaleX + blurScaleY) / 2;
    const adjustments = {
      brightness: layer.brightness,
      contrast: layer.contrast,
      saturation: layer.saturation,
      hue: layer.hue,
      blurRadius,
    };
    const key = `${layer.sourceUrl}|${layer.whiteRemoval}|${layer.whiteThreshold}|${layer.maskStrokes.length}|${layer.brightness}|${layer.contrast}|${layer.saturation}|${layer.hue}|${blurRadius.toFixed(3)}`;
    const adjusted = this.fallbackAdjustedCanvas(source, adjustments, key, this.layerAdjustmentCache.get(layer.id) || null);
    if (adjusted.cache) this.layerAdjustmentCache.set(layer.id, adjusted.cache);
    else this.layerAdjustmentCache.delete(layer.id);
    return adjusted.source;
  }

  private fitDisplay(): void {
    if (!this.photo || !this.canvas.parentElement) return;
    const parent = this.canvas.parentElement;
    const styles = getComputedStyle(parent);
    const availableWidth = parent.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    const availableHeight = parent.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
    const ratio = Math.min(availableWidth / this.canvas.width, availableHeight / this.canvas.height);
    this.canvas.style.width = `${Math.max(1, this.canvas.width * ratio)}px`;
    this.canvas.style.height = `${Math.max(1, this.canvas.height * ratio)}px`;
  }

  private processedBase(layer: TattooLayer): HTMLCanvasElement {
    const source = this.images.get(layer.sourceUrl);
    if (!source) throw new Error(`Missing layer source: ${layer.sourceUrl}`);
    const key = this.baseCacheKey(layer);
    const cached = this.baseCache.get(key);
    if (cached) return cached;
    const { width, height } = sourceSize(source);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: layer.whiteRemoval })!;
    context.drawImage(source, 0, 0, width, height);
    if (layer.whiteRemoval) {
      const pixels = context.getImageData(0, 0, width, height);
      const threshold = layer.whiteThreshold;
      let analysis = this.backgroundAnalysisCache.get(layer.sourceUrl);
      if (!analysis || analysis.width !== width || analysis.height !== height) {
        analysis = analyzeArtworkBackground(pixels.data, width, height);
        this.backgroundAnalysisCache.set(layer.sourceUrl, analysis);
        this.backgroundAnalysisBuilds += 1;
      }
      applyArtworkBackgroundAnalysis(pixels.data, threshold, analysis);
      context.putImageData(pixels, 0, 0);
    }
    this.baseCache.set(key, canvas);
    return canvas;
  }

  private preparedLayer(layer: TattooLayer): HTMLCanvasElement {
    const strokeKey = layer.maskStrokes.map((stroke) => `${stroke.mode}:${stroke.size}:${stroke.points.map((point) => `${point.x.toFixed(3)},${point.y.toFixed(3)}`).join(';')}`).join('|');
    const key = `${layer.sourceUrl}|${layer.whiteRemoval}|${layer.whiteThreshold}|${strokeKey}`;
    const cached = this.layerCache.get(layer.id);
    if (cached?.key === key) return cached.canvas;
    const base = this.processedBase(layer);
    if (!layer.maskStrokes.length) {
      this.layerCache.set(layer.id, { key, canvas: base });
      return base;
    }
    const canvas = document.createElement('canvas');
    canvas.width = base.width;
    canvas.height = base.height;
    const context = canvas.getContext('2d')!;
    context.drawImage(base, 0, 0);
    const mask = document.createElement('canvas');
    mask.width = base.width;
    mask.height = base.height;
    const maskContext = mask.getContext('2d')!;
    maskContext.fillStyle = '#fff';
    maskContext.fillRect(0, 0, mask.width, mask.height);
    for (const stroke of layer.maskStrokes) {
      maskContext.save();
      maskContext.globalCompositeOperation = stroke.mode === 'erase' ? 'destination-out' : 'source-over';
      maskContext.strokeStyle = '#fff';
      maskContext.fillStyle = '#fff';
      maskContext.lineCap = 'round';
      maskContext.lineJoin = 'round';
      maskContext.lineWidth = Math.max(2, Math.min(canvas.width, canvas.height) * stroke.size / 100);
      maskContext.beginPath();
      stroke.points.forEach((point, index) => {
        const x = point.x * canvas.width;
        const y = point.y * canvas.height;
        if (index === 0) maskContext.moveTo(x, y); else maskContext.lineTo(x, y);
      });
      if (stroke.points.length === 1) {
        const point = stroke.points[0];
        maskContext.arc(point.x * canvas.width, point.y * canvas.height, maskContext.lineWidth / 2, 0, Math.PI * 2);
        maskContext.fill();
      } else maskContext.stroke();
      maskContext.restore();
    }
    context.globalCompositeOperation = 'destination-in';
    context.drawImage(mask, 0, 0);
    context.globalCompositeOperation = 'source-over';
    releaseCanvas(mask);
    this.layerCache.set(layer.id, { key, canvas });
    return canvas;
  }

  private layerGeometry(layer: TattooLayer, width = this.canvas.width, height = this.canvas.height): LayerGeometry {
    const source = this.images.get(layer.sourceUrl);
    const size = source ? sourceSize(source) : { width: 1, height: 1 };
    const baseHeight = Math.min(width, height) * layer.scale / 100;
    const drawWidth = baseHeight * (size.width / Math.max(1, size.height)) * layer.scaleX / 100;
    const drawHeight = baseHeight * layer.scaleY / 100;
    const center = normalizedToCanvasPoint(layer, width, height);
    const matrix = new DOMMatrix()
      .translate(center.x, center.y)
      .rotate(layer.rotation)
      .multiply(new DOMMatrix([1, Math.tan(radians(layer.skewY)), Math.tan(radians(layer.skewX)), 1, 0, 0]))
      .scale(layer.flipX, layer.flipY);
    const localCorners = [
      { x: -drawWidth / 2, y: -drawHeight / 2 }, { x: drawWidth / 2, y: -drawHeight / 2 },
      { x: drawWidth / 2, y: drawHeight / 2 }, { x: -drawWidth / 2, y: drawHeight / 2 },
    ];
    const transform = (point: Point): Point => {
      const output = new DOMPoint(point.x, point.y).matrixTransform(matrix);
      return { x: output.x, y: output.y };
    };
    return {
      width: drawWidth,
      height: drawHeight,
      matrix,
      inverse: matrix.inverse(),
      corners: localCorners.map(transform),
      rotationHandle: transform({ x: 0, y: -drawHeight / 2 - Math.max(38, Math.min(width, height) * .04) }),
    };
  }

  private drawPhoto(context: CanvasRenderingContext2D, width: number, height: number): void {
    if (!this.photo) return;
    const photo = this.state.photo;
    const source = this.adjustedPhotoSource(photo);
    const sx = width / this.canvas.width;
    const sy = height / this.canvas.height;
    context.save();
    context.translate(width / 2 + photo.offsetX * sx, height / 2 + photo.offsetY * sy);
    context.rotate(radians(photo.rotation));
    context.scale(photo.flipX * photo.zoom / 100, photo.zoom / 100);
    if (this.nativeCanvasFilters) context.filter = `brightness(${photo.brightness / 100}) contrast(${photo.contrast / 100}) saturate(${photo.saturation / 100})`;
    context.drawImage(source, -source.width * sx / 2, -source.height * sy / 2, source.width * sx, source.height * sy);
    context.restore();
    if (photo.warmth !== 0) {
      context.save();
      context.globalCompositeOperation = 'soft-light';
      context.globalAlpha = Math.abs(photo.warmth) / 100;
      context.fillStyle = photo.warmth > 0 ? '#ff8a3d' : '#4b76cc';
      context.fillRect(0, 0, width, height);
      context.restore();
    }
    this.flushAdjustmentDiagnostics('photo', 'photo');
  }

  private drawLayer(context: CanvasRenderingContext2D, layer: TattooLayer, width: number, height: number): void {
    if (!layer.visible) return;
    const prepared = this.preparedLayer(layer);
    const source = this.adjustedLayerSource(layer, prepared);
    const geometry = this.layerGeometry(layer, width, height);
    context.save();
    context.setTransform(geometry.matrix);
    context.globalAlpha = layer.opacity / 100;
    context.globalCompositeOperation = layer.blendMode;
    if (this.nativeCanvasFilters) context.filter = `brightness(${layer.brightness / 100}) contrast(${layer.contrast / 100}) saturate(${layer.saturation / 100}) hue-rotate(${layer.hue}deg) blur(${layer.softness * width / this.canvas.width}px)`;
    context.drawImage(source, -geometry.width / 2, -geometry.height / 2, geometry.width, geometry.height);
    context.restore();
    this.flushAdjustmentDiagnostics('layer', layer.id);
  }

  private drawSelection(context: CanvasRenderingContext2D, width: number, height: number): void {
    const layer = this.getSelectedLayer();
    if (!layer || !layer.visible || this.previewMode) return;
    const geometry = this.layerGeometry(layer, width, height);
    const unit = width / Math.max(1, this.canvas.getBoundingClientRect().width || width);
    context.save();
    context.strokeStyle = layer.locked ? '#8596A8' : '#3978E8';
    context.lineWidth = Math.max(1.5, 1.5 * unit);
    context.setLineDash([7 * unit, 6 * unit]);
    context.beginPath();
    geometry.corners.forEach((corner, index) => index ? context.lineTo(corner.x, corner.y) : context.moveTo(corner.x, corner.y));
    context.closePath();
    context.stroke();
    context.setLineDash([]);
    const topMid = { x: (geometry.corners[0].x + geometry.corners[1].x) / 2, y: (geometry.corners[0].y + geometry.corners[1].y) / 2 };
    context.beginPath(); context.moveTo(topMid.x, topMid.y); context.lineTo(geometry.rotationHandle.x, geometry.rotationHandle.y); context.stroke();
    for (const point of [...geometry.corners, geometry.rotationHandle]) {
      context.beginPath();
      context.arc(point.x, point.y, Math.max(6, 6 * unit), 0, Math.PI * 2);
      context.fillStyle = '#111C26';
      context.fill();
      context.strokeStyle = layer.locked ? '#8596A8' : '#3978E8';
      context.stroke();
    }
    context.restore();
  }

  private drawWatermark(context: CanvasRenderingContext2D, width: number, height: number): void {
    if (!this.photo) return;
    context.save();
    context.globalAlpha = .12;
    const columns = width >= height ? 4 : 3;
    const rows = width >= height ? 4 : 5;
    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        const x = width * ((column + .5 + (row % 2 ? .2 : 0)) / columns);
        const y = height * ((row + .5) / rows);
        context.save();
        context.translate(x, y);
        context.rotate(-Math.PI / 10);
        if (this.watermark.complete && this.watermark.naturalWidth) {
          const markWidth = Math.min(width / (columns + 1), 210 * width / this.canvas.width);
          const markHeight = markWidth * .63;
          context.drawImage(this.watermark, -markWidth / 2, -markHeight / 2, markWidth, markHeight);
        } else {
          context.fillStyle = '#F5F7FA';
          context.font = `600 ${Math.max(18, width * .035)}px system-ui`;
          context.textAlign = 'center';
          context.fillText('WALLD', 0, 0);
        }
        context.restore();
      }
    }
    context.restore();
  }

  private drawScene(context: CanvasRenderingContext2D, width: number, height: number, options: { selection: boolean; watermark: boolean }): void {
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalAlpha = 1;
    context.globalCompositeOperation = 'source-over';
    if (this.nativeCanvasFilters) context.filter = 'none';
    context.fillStyle = '#101820';
    context.fillRect(0, 0, width, height);
    this.drawPhoto(context, width, height);
    for (const layer of this.state.layers) this.drawLayer(context, layer, width, height);
    if (options.watermark) this.drawWatermark(context, width, height);
    if (options.selection) this.drawSelection(context, width, height);
    context.restore();
  }

  render(): void {
    if (this.destroyed) return;
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (this.destroyed) return;
      try { this.drawScene(this.context, this.canvas.width, this.canvas.height, { selection: true, watermark: true }); }
      catch (error) { this.callbacks.onError?.(error instanceof Error ? error.message : 'Rendering failed.'); }
    });
  }

  async exportBlob(options: { maxEdge?: number; quality?: number; watermark?: boolean } = {}): Promise<Blob> {
    if (!this.photo) throw new Error('Upload a photo first.');
    const maxEdge = options.maxEdge ?? (isCoarsePointer() ? 1800 : 2400);
    const ratio = Math.min(1, maxEdge / Math.max(this.canvas.width, this.canvas.height));
    const output = document.createElement('canvas');
    output.width = Math.max(1, Math.round(this.canvas.width * ratio));
    output.height = Math.max(1, Math.round(this.canvas.height * ratio));
    this.drawScene(output.getContext('2d')!, output.width, output.height, { selection: false, watermark: options.watermark ?? false });
    try {
      return await new Promise((resolve, reject) => output.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Export failed.')), 'image/jpeg', options.quality ?? .93));
    } finally {
      releaseCanvas(output);
    }
  }

  private listen<K extends keyof HTMLElementEventMap>(type: K, listener: (event: HTMLElementEventMap[K]) => void): void {
    const eventListener = listener as EventListener;
    this.canvas.addEventListener(type, eventListener);
    this.eventListeners.push({ type, listener: eventListener });
  }

  private canvasPoint(event: PointerEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    return { x: (event.clientX - rect.left) * this.canvas.width / rect.width, y: (event.clientY - rect.top) * this.canvas.height / rect.height };
  }

  private localPoint(layer: TattooLayer, point: Point): Point {
    const local = new DOMPoint(point.x, point.y).matrixTransform(this.layerGeometry(layer).inverse);
    return { x: local.x, y: local.y };
  }

  private layerAtPoint(point: Point): TattooLayer | null {
    for (const layer of frontToBack(this.state.layers)) {
      if (!layer.visible) continue;
      const local = this.localPoint(layer, point);
      const geometry = this.layerGeometry(layer);
      if (Math.abs(local.x) <= geometry.width / 2 && Math.abs(local.y) <= geometry.height / 2) return layer;
    }
    return null;
  }

  private hitSelected(point: Point): 'outside' | 'move' | 'scale' | 'rotate' {
    const layer = this.getSelectedLayer();
    if (!layer || !layer.visible) return 'outside';
    const geometry = this.layerGeometry(layer);
    const unit = this.canvas.width / Math.max(1, this.canvas.getBoundingClientRect().width);
    const radius = 18 * unit;
    if (geometry.corners.some((corner) => distance(corner, point) <= radius)) return 'scale';
    if (distance(geometry.rotationHandle, point) <= radius) return 'rotate';
    const local = this.localPoint(layer, point);
    return Math.abs(local.x) <= geometry.width / 2 + radius && Math.abs(local.y) <= geometry.height / 2 + radius ? 'move' : 'outside';
  }

  private normalizedMaskPoint(layer: TattooLayer, point: Point): Point | null {
    const local = this.localPoint(layer, point);
    const geometry = this.layerGeometry(layer);
    const normalized = { x: local.x / geometry.width + .5, y: local.y / geometry.height + .5 };
    return normalized.x >= 0 && normalized.x <= 1 && normalized.y >= 0 && normalized.y <= 1 ? normalized : null;
  }

  private hideBrushCursor(): void {
    this.canvas.parentElement?.classList.remove('has-brush-cursor');
  }

  private updateBrushCursor(event: PointerEvent): void {
    const parent = this.canvas.parentElement;
    const layer = this.getSelectedLayer();
    const source = layer && this.images.get(layer.sourceUrl);
    if (!parent || !layer || !source || this.interactionMode.value === 'off' || event.pointerType === 'touch') {
      this.hideBrushCursor();
      return;
    }
    const parentRect = parent.getBoundingClientRect();
    const canvasRect = this.canvas.getBoundingClientRect();
    const geometry = this.layerGeometry(layer);
    const size = sourceSize(source);
    const sourceBrush = Math.min(size.width, size.height) * this.maskBrushSize / 100;
    const xRatio = geometry.width / Math.max(1, size.width);
    const yRatio = geometry.height / Math.max(1, size.height);
    const displayRatio = ((Math.abs(xRatio) + Math.abs(yRatio)) / 2) * canvasRect.width / Math.max(1, this.canvas.width);
    const diameter = clamp(sourceBrush * displayRatio, 6, Math.min(parentRect.width, parentRect.height) * .8);
    parent.style.setProperty('--brush-x', `${event.clientX - parentRect.left}px`);
    parent.style.setProperty('--brush-y', `${event.clientY - parentRect.top}px`);
    parent.style.setProperty('--brush-size', `${diameter}px`);
    parent.classList.add('has-brush-cursor');
  }

  private bindGestures(): void {
    this.canvas.style.touchAction = 'none';
    this.listen('pointerdown', (event) => {
      if (this.previewMode || !this.photo) return;
      const point = this.canvasPoint(event);
      let selected = this.getSelectedLayer();
      const hitLayer = this.layerAtPoint(point);
      if (hitLayer && hitLayer.id !== selected?.id) {
        this.selectLayer(hitLayer.id);
        selected = hitLayer;
      }
      if (!selected) return;
      if (this.interactionMode.value !== 'off' && !selected.locked) {
        const maskPoint = this.normalizedMaskPoint(selected, point);
        if (!maskPoint) return;
        event.preventDefault();
        this.beginHistoryTransaction();
        this.canvas.setPointerCapture(event.pointerId);
        selected.maskStrokes.push({ mode: this.interactionMode.value, size: this.maskBrushSize, points: [maskPoint] });
        this.invalidateLayer(selected.id);
        this.gesture = { type: 'mask', strokeIndex: selected.maskStrokes.length - 1 };
        this.pointers.set(event.pointerId, point);
        this.callbacks.onInteractionActive?.(true);
        this.callbacks.onMaskStrokeStart?.();
        this.render();
        return;
      }
      const hit = this.hitSelected(point);
      if (hit === 'outside' || selected.locked) return;
      event.preventDefault();
      this.beginHistoryTransaction();
      this.canvas.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, point);
      this.callbacks.onInteractionActive?.(true);
      const values = [...this.pointers.values()];
      if (values.length === 2) this.gesture = { type: 'pinch', distance: distance(values[0], values[1]), angle: angle(values[0], values[1]), scale: selected.scale, rotation: selected.rotation };
      else if (hit === 'move') this.gesture = { type: 'drag', origin: point, layerX: selected.x, layerY: selected.y };
      else if (hit === 'scale') this.gesture = { type: 'scale', originDistance: Math.max(1, distance(point, { x: selected.x * this.canvas.width, y: selected.y * this.canvas.height })), scale: selected.scale };
      else this.gesture = { type: 'rotate', originAngle: angle({ x: selected.x * this.canvas.width, y: selected.y * this.canvas.height }, point), rotation: selected.rotation };
    });

    this.listen('pointermove', (event) => {
      this.updateBrushCursor(event);
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      event.preventDefault();
      const point = this.canvasPoint(event);
      this.pointers.set(event.pointerId, point);
      const layer = this.getSelectedLayer();
      if (!layer || layer.locked) return;
      if (this.gesture.type === 'mask') {
        const normalized = this.normalizedMaskPoint(layer, point);
        if (normalized) layer.maskStrokes[this.gesture.strokeIndex]?.points.push(normalized);
        this.invalidateLayer(layer.id);
      } else {
        const values = [...this.pointers.values()];
        if (values.length === 2) {
          if (this.gesture.type !== 'pinch') this.gesture = { type: 'pinch', distance: distance(values[0], values[1]), angle: angle(values[0], values[1]), scale: layer.scale, rotation: layer.rotation };
          const pinch = this.gesture as Extract<Gesture, { type: 'pinch' }>;
          layer.scale = clamp(pinch.scale * distance(values[0], values[1]) / Math.max(1, pinch.distance), 3, 250);
          layer.rotation = normalizeAngle(pinch.rotation + angle(values[0], values[1]) - pinch.angle);
        } else if (this.gesture.type === 'drag') {
          const translated = translateNormalizedPoint(
            { x: this.gesture.layerX, y: this.gesture.layerY },
            { x: point.x - this.gesture.origin.x, y: point.y - this.gesture.origin.y },
            this.canvas.width,
            this.canvas.height,
          );
          layer.x = translated.x;
          layer.y = translated.y;
        } else if (this.gesture.type === 'scale') {
          layer.scale = clamp(this.gesture.scale * distance(point, { x: layer.x * this.canvas.width, y: layer.y * this.canvas.height }) / this.gesture.originDistance, 3, 250);
        } else if (this.gesture.type === 'rotate') {
          layer.rotation = normalizeAngle(this.gesture.rotation + angle({ x: layer.x * this.canvas.width, y: layer.y * this.canvas.height }, point) - this.gesture.originAngle);
        }
      }
      this.render();
      this.notify();
    });

    const end = (event: PointerEvent) => {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      this.pointers.delete(event.pointerId);
      if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
      if (this.pointers.size === 1) {
        const layer = this.getSelectedLayer();
        const point = [...this.pointers.values()][0];
        if (layer) this.gesture = { type: 'drag', origin: point, layerX: layer.x, layerY: layer.y };
      } else if (!this.pointers.size) {
        this.gesture = null;
        this.callbacks.onInteractionActive?.(false);
        this.commitHistoryTransaction();
      }
    };
    this.listen('pointerup', end);
    this.listen('pointercancel', end);
    this.listen('pointerleave', () => { if (!this.pointers.size) this.hideBrushCursor(); });
  }

  private bindKeyboard(): void {
    this.listen('keydown', (event) => {
      const layer = this.getSelectedLayer();
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (this.history.transactionActive) this.commitHistoryTransaction();
        if (event.shiftKey) void this.redo(); else void this.undo();
        return;
      }
      if (!layer || layer.locked) return;
      const stepX = (event.shiftKey ? 10 : 2) / Math.max(1, this.canvas.width);
      const stepY = (event.shiftKey ? 10 : 2) / Math.max(1, this.canvas.height);
      if (event.key.startsWith('Arrow')) this.beginHistoryTransaction();
      if (event.key === 'ArrowLeft') layer.x -= stepX;
      else if (event.key === 'ArrowRight') layer.x += stepX;
      else if (event.key === 'ArrowUp') layer.y -= stepY;
      else if (event.key === 'ArrowDown') layer.y += stepY;
      else if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); this.deleteSelected(); return; }
      else return;
      event.preventDefault();
      this.render();
      this.notify();
    });
    this.listen('keyup', (event) => {
      if (event.key.startsWith('Arrow') && this.history.transactionActive) this.commitHistoryTransaction();
    });
    this.listen('blur', () => {
      if (this.history.transactionActive && !this.pointers.size) this.commitHistoryTransaction();
      this.hideBrushCursor();
    });
  }
}
